import { memo, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";

import { URLS } from "config/urls";
import {
  AddIcon,
  GridViewSharpIcon,
  PlaylistAddIcon,
  RoomOutlinedIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { reverse } from "utils/urls";

import Box from "components/Box";
import Button from "components/Button";
import ButtonDropdown from "components/ButtonDropdown";
import ButtonGroup from "components/ButtonGroup";
import { MenuItem } from "components/Menu";
import PageHeader from "components/PageHeader";
import Select from "components/Select";
import Stack from "components/Stack";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";

import { useCompanySettings } from "entities/Company/sdk";
import AddDealButton from "entities/Deal/components/AddDealButton";
import AddDealImportButton from "entities/Deal/components/AddDealImportButton";
import ExportButton from "entities/Deal/components/ExportButton";
import { Phase, ProjectsViewType } from "entities/Deal/constants";
import { IPipelineColumns } from "entities/Deal/sdk";
import FiltersButton from "entities/Reporting/components/FiltersButton";
import ReportSearchField from "entities/Reporting/components/ReportSearchField";
import SelectedFilters from "entities/Reporting/components/SelectedFilters";

const PHASES = [
  "All",
  Phase.APPLICATION,
  Phase.UNDERWRITING,
  Phase.CONSTRUCTION,
  Phase.CONVERSION,
  Phase.ASSET_MANAGEMENT,
];

export interface IProjectsPageHeader {
  rows: { [key: string]: string | null }[];
  columns: Array<IPipelineColumns>;
  phase: string;
  selectedView: ProjectsViewType;
  loading: boolean;
  onChangeView: (view: ProjectsViewType) => void;
  onCreateDeal: () => void;
}

const ProjectsPageHeader = ({
  rows,
  columns,
  phase,
  selectedView,
  loading,
  onChangeView,
  onCreateDeal,
}: IProjectsPageHeader) => {
  const navigate = useNavigate();
  const { data: companySettings } = useCompanySettings();

  const phaseOptions = useMemo(
    () => PHASES.map((phase) => ({ value: phase, label: phase })),
    []
  );

  const handleViewChange = useCallback(
    (viewType: ProjectsViewType) => () => {
      onChangeView(viewType);
      navigate(reverse(URLS.PROJECTS_LIST_VIEW, { view: viewType, phase }));
    },
    [navigate, phase, onChangeView]
  );

  const handlePhaseSwitch = useCallback(
    (event: any) => {
      navigate(
        URLS.PROJECTS_LIST_VIEW.replace(":phase", event.target.value).replace(
          ":view",
          selectedView
        )
      );
    },
    [navigate, selectedView]
  );

  const handleCreatedDeal = useCallback(() => {
    // TODO: update list data. It's updated by reporting and background tasks.
    toast.successMessage("Deal created successfully!");
    onCreateDeal();
  }, [onCreateDeal]);

  return (
    <>
      <PageHeader
        title="Deals dashboard"
        dataTestid="deals-dashboard-section"
        actions={
          <Stack
            spacing={2}
            direction="row"
            justifyContent="space-between"
            sx={{ flex: 1 }}
          >
            <Stack spacing={2} direction="row">
              <Select
                size="small"
                sx={{ width: "154px" }}
                label="PHASE"
                options={phaseOptions}
                inputProps={{
                  "data-testid": "dashboard-deal-phase-dropdown-input",
                  sx: {
                    backgroundColor: "white",
                  },
                }}
                data-testid="dashboard-deal-phase-dropdown"
                dropdownOptionsDataTestid="dashboard-deal-phase-dropdown-option"
                onChange={handlePhaseSwitch}
                value={phase}
              />
              <ButtonGroup
                color="secondary"
                data-testid="dashboard-view-buttons-group"
                disableRipple
              >
                <Tooltip title="Grid view">
                  <Button
                    variant={
                      selectedView === ProjectsViewType.grid
                        ? "contained"
                        : "outlined"
                    }
                    sx={{
                      padding: theme.spacing(1),
                      border: `1px solid ${colors.blue60}`,
                    }}
                    onClick={handleViewChange(ProjectsViewType.grid)}
                    data-testid="dashboard-grid-view-button"
                  >
                    <GridViewSharpIcon
                      sx={{
                        width: 20,
                        height: 20,
                        opacity:
                          selectedView === ProjectsViewType.grid ? 1 : 0.5,
                      }}
                    />
                  </Button>
                </Tooltip>
                <Tooltip title="Detailed list view">
                  <Button
                    variant={
                      selectedView === ProjectsViewType.detailedList
                        ? "contained"
                        : "outlined"
                    }
                    sx={{
                      padding: theme.spacing(1),
                      border: `1px solid ${colors.blue60}`,
                    }}
                    onClick={handleViewChange(ProjectsViewType.detailedList)}
                    data-testid="dashboard-list-view-button"
                  >
                    <PlaylistAddIcon
                      sx={{
                        width: 25,
                        height: 25,
                        opacity:
                          selectedView === ProjectsViewType.detailedList
                            ? 1
                            : 0.5,
                      }}
                    />
                  </Button>
                </Tooltip>
                <Tooltip title="Map view">
                  <Button
                    variant={
                      selectedView === ProjectsViewType.map
                        ? "contained"
                        : "outlined"
                    }
                    sx={{
                      padding: theme.spacing(1),
                      border: `1px solid ${colors.blue60}`,
                    }}
                    onClick={handleViewChange(ProjectsViewType.map)}
                    data-testid="dashboard-map-view-button"
                  >
                    <RoomOutlinedIcon
                      sx={{
                        width: 20,
                        height: 20,
                        opacity:
                          selectedView === ProjectsViewType.map ? 1 : 0.5,
                      }}
                    />
                  </Button>
                </Tooltip>
              </ButtonGroup>
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              sx={{ flex: 1 }}
              justifyContent="flex-end"
            >
              <Box sx={{ maxWidth: 416, flex: 1 }}>
                <ReportSearchField placeholder="Search by deal name, city, or state" />
              </Box>
              <FiltersButton
                reportPackageId={null}
                disabled={loading}
                rows={rows}
                filtersButtonDataTestid="filter-deals-button"
              />
              <ExportButton rows={rows} columns={columns} disabled={loading} />
              {companySettings?.bulk_deal_creation_shown ? (
                <ButtonDropdown
                  startIcon={<AddIcon />}
                  label="New deal"
                  variant="contained"
                  color="primary"
                  keepMenuMounted={true}
                  sx={{
                    padding: theme.spacing(1, 2),
                    "& .MuiButton-endIcon": {
                      margin: 0,
                    },
                    maxWidth: "fit-content",
                    alignSelf: "center",
                    justifySelf: "flex-end",
                  }}
                  buttonDataTestid="new-deal-button"
                >
                  <MenuItem
                    sx={{ padding: theme.spacing(1, 2) }}
                    data-testid="add-deal-option"
                  >
                    <AddDealButton
                      isButtonWrapped
                      onCreated={handleCreatedDeal}
                    />
                  </MenuItem>
                  <MenuItem
                    sx={{ padding: theme.spacing(1, 2) }}
                    data-testid="add-deal-import-option"
                  >
                    <AddDealImportButton onCreated={onCreateDeal} />
                  </MenuItem>
                </ButtonDropdown>
              ) : (
                <AddDealButton
                  onCreated={handleCreatedDeal}
                  isButtonWrapped={false}
                />
              )}
            </Stack>
          </Stack>
        }
      />
      {!loading && (
        <SelectedFilters
          columns={columns}
          reportPackageId={null}
          isHardcodedReport={false}
        />
      )}
    </>
  );
};

export default memo(
  ProjectsPageHeader,
  (prevProps, nextProps) =>
    prevProps.rows === nextProps.rows &&
    prevProps.columns === nextProps.columns &&
    prevProps.phase === nextProps.phase &&
    prevProps.selectedView === nextProps.selectedView &&
    prevProps.loading === nextProps.loading
);
